import { initializeApp } from "firebase/app";
import { getMessaging, getToken, type Messaging } from "firebase/messaging";
import {
  FIREBASE_API_KEY,
  FIREBASE_AUTH_DOMAIN,
  FIREBASE_PROJECT_ID,
  FIREBASE_MESSAGING_SENDER_ID,
  FIREBASE_APP_ID,
  FIREBASE_VAPID_KEY,
} from "@/constants";

const firebaseConfig = {
  apiKey: FIREBASE_API_KEY,
  authDomain: FIREBASE_AUTH_DOMAIN,
  projectId: FIREBASE_PROJECT_ID,
  messagingSenderId: FIREBASE_MESSAGING_SENDER_ID,
  appId: FIREBASE_APP_ID,
};

export const firebaseApp = initializeApp(firebaseConfig);

// Lazy-initialized messaging instance
// getMessaging() requires navigator.serviceWorker which doesn't exist in iOS in-app browsers
let _messaging: Messaging | null = null;

function getMessagingInstance(): Messaging | null {
  if (_messaging) return _messaging;
  
  // Guard: serviceWorker not available in iOS in-app browsers (Instagram, Facebook, etc.)
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    console.warn('[Firebase] Service workers not supported - push notifications disabled');
    return null;
  }
  
  try {
    _messaging = getMessaging(firebaseApp);
    return _messaging;
  } catch (error) {
    console.warn('[Firebase] Failed to initialize messaging:', error);
    return null;
  }
}

export async function requestFCMToken(): Promise<string | null> {
  // Guard: Check for Notification API support
  if (typeof Notification === 'undefined') {
    console.warn('[Firebase] Notifications API not supported');
    return null;
  }
  
  const messaging = getMessagingInstance();
  if (!messaging) return null;
  
  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return null;

    return await getToken(messaging, {
      vapidKey: FIREBASE_VAPID_KEY,
    });
  } catch (error) {
    console.warn('[Firebase] Failed to get FCM token:', error);
    return null;
  }
}
